import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { useAuth } from "../contexts/AuthContext";
import ClaimArtistFlow from "../components/ClaimArtistFlow";
import { PROS } from "../lib/statuses";
import { toast } from "sonner";

export default function Wizard() {
  const { user, profile, loading, updateProfile } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);

  // Step 1: identity
  const [artistName, setArtistName] = useState("");
  const [stageName, setStageName] = useState("");
  const [handle, setHandle] = useState("");
  // Step 3: defaults
  const [defaults, setDefaults] = useState({
    default_pro: "",
    default_publisher: "",
    default_label: "",
    default_distributor: "",
  });
  // Step 4: bio
  const [bio, setBio] = useState("");

  useEffect(() => {
    if (!loading && !user) nav("/auth");
    if (profile) {
      setArtistName(profile.artist_name || "");
      setStageName(profile.stage_name || "");
      setHandle(profile.social_handles?.primary || "");
      setBio(profile.bio || "");
      setDefaults({
        default_pro: profile.default_pro || "",
        default_publisher: profile.default_publisher || "",
        default_label: profile.default_label || "",
        default_distributor: profile.default_distributor || "",
      });
    }
  }, [loading, user, profile, nav]);

  const saveStep1 = async () => {
    if (!artistName.trim()) {
      toast.error("Artist name is required");
      return;
    }
    await updateProfile({
      artist_name: artistName.trim(),
      stage_name: stageName.trim(),
      social_handles: { ...(profile?.social_handles || {}), primary: handle.trim() },
    });
    setStep(2);
  };

  const saveStep3 = async () => {
    await updateProfile(defaults);
    setStep(4);
  };

  const finish = async () => {
    await updateProfile({ bio });
    toast.success("Profile saved. Welcome to Lacquer.");
    nav("/dashboard");
  };

  return (
    <>
      <Header />
      <div className="lq-container page-enter" data-testid="wizard-page" style={{ maxWidth: 720 }}>
        <div className="wizard-progress" data-testid="wizard-progress">
          {[1, 2, 3, 4].map((n) => (
            <div key={n} className={`wizard-step ${step === n ? "active" : ""} ${step > n ? "done" : ""}`} />
          ))}
        </div>

        {step === 1 && (
          <Step1
            artistName={artistName} setArtistName={setArtistName}
            stageName={stageName} setStageName={setStageName}
            handle={handle} setHandle={setHandle}
            onContinue={saveStep1} onSkip={() => nav("/dashboard")}
          />
        )}

        {step === 2 && (
          <Step2
            defaultName={artistName || stageName}
            onContinue={() => setStep(3)}
          />
        )}

        {step === 3 && (
          <Step3
            defaults={defaults} setDefaults={setDefaults}
            onBack={() => setStep(2)} onContinue={saveStep3} onSkip={() => setStep(4)}
          />
        )}

        {step === 4 && (
          <Step4
            bio={bio} setBio={setBio}
            onBack={() => setStep(3)} onFinish={finish}
          />
        )}
      </div>
    </>
  );
}

function Step1({ artistName, setArtistName, stageName, setStageName, handle, setHandle, onContinue, onSkip }) {
  return (
    <div data-testid="wizard-step-1">
      <div className="eyebrow" style={{ color: "var(--accent)" }}>— Profile · Step 1 of 4</div>
      <h2 className="serif" style={{ fontSize: 38, lineHeight: 1.05, margin: "10px 0 14px" }}>
        Who are you, as an artist?
      </h2>
      <p style={{ color: "var(--text-mute)", fontSize: 14.5, marginBottom: 26 }}>
        This information personalizes your catalog and auto-fills as defaults on new tracks. You can change any of it later from Settings.
      </p>

      <div style={{ marginBottom: 14 }}>
        <label className="label">Artist Name *</label>
        <input className="lq-input" value={artistName} onChange={(e) => setArtistName(e.target.value)} placeholder="Your performing name" data-testid="wizard-artist-name" />
      </div>
      <div className="settings-grid-2">
        <div>
          <label className="label">Stage Name (optional)</label>
          <input className="lq-input" value={stageName} onChange={(e) => setStageName(e.target.value)} placeholder="If different" data-testid="wizard-stage-name" />
        </div>
        <div>
          <label className="label">Primary Social Handle</label>
          <input className="lq-input" value={handle} onChange={(e) => setHandle(e.target.value)} placeholder="@yourhandle" data-testid="wizard-handle" />
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 28, gap: 10 }}>
        <button className="btn btn-ghost" onClick={onSkip} data-testid="wizard-skip-setup">Skip Setup</button>
        <button className="btn btn-primary" onClick={onContinue} data-testid="wizard-step1-continue">Continue →</button>
      </div>
    </div>
  );
}

function Step2({ defaultName, onContinue }) {
  return (
    <div data-testid="wizard-step-2">
      <div className="eyebrow" style={{ color: "var(--accent)" }}>— Profile · Step 2 of 4</div>
      <ClaimArtistFlow
        defaultName={defaultName}
        embedded
        onClose={onContinue}
        onDone={onContinue}
      />
    </div>
  );
}

function Step3({ defaults, setDefaults, onBack, onContinue, onSkip }) {
  const set = (k, v) => setDefaults({ ...defaults, [k]: v });
  return (
    <div data-testid="wizard-step-3">
      <div className="eyebrow" style={{ color: "var(--accent)" }}>— Profile · Step 3 of 4</div>
      <h2 className="serif" style={{ fontSize: 38, lineHeight: 1.05, margin: "10px 0 14px" }}>
        Your registration defaults
      </h2>
      <p style={{ color: "var(--text-mute)", fontSize: 14.5, marginBottom: 26 }}>
        Every new track will be created with these defaults pre-filled. Override per-track when needed.
      </p>

      <div className="settings-grid-2">
        <div>
          <label className="label">Default PRO</label>
          <select className="lq-select" value={defaults.default_pro} onChange={(e) => set("default_pro", e.target.value)} data-testid="wizard-default-pro">
            {PROS.map((p) => <option key={p} value={p}>{p || "—"}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Default Publisher</label>
          <input className="lq-input" value={defaults.default_publisher} onChange={(e) => set("default_publisher", e.target.value)} placeholder="Self-published" />
        </div>
        <div>
          <label className="label">Default Label</label>
          <input className="lq-input" value={defaults.default_label} onChange={(e) => set("default_label", e.target.value)} placeholder="Independent" />
        </div>
        <div>
          <label className="label">Default Distributor</label>
          <input className="lq-input" value={defaults.default_distributor} onChange={(e) => set("default_distributor", e.target.value)} placeholder="DistroKid, TuneCore, etc." />
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 28, gap: 10 }}>
        <button className="btn btn-ghost" onClick={onBack}>← Back</button>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn btn-ghost" onClick={onSkip}>Skip</button>
          <button className="btn btn-primary" onClick={onContinue} data-testid="wizard-step3-continue">Continue →</button>
        </div>
      </div>
    </div>
  );
}

function Step4({ bio, setBio, onBack, onFinish }) {
  return (
    <div data-testid="wizard-step-4">
      <div className="eyebrow" style={{ color: "var(--accent)" }}>— Profile · Step 4 of 4</div>
      <h2 className="serif" style={{ fontSize: 38, lineHeight: 1.05, margin: "10px 0 14px" }}>
        A short bio (optional)
      </h2>
      <p style={{ color: "var(--text-mute)", fontSize: 14.5, marginBottom: 26 }}>
        Used for any future public-facing share view — fully optional. You can always edit this later.
      </p>

      <div>
        <label className="label">Artist Bio</label>
        <textarea className="lq-textarea" rows={5} value={bio} onChange={(e) => setBio(e.target.value)} placeholder="One or two sentences. Style, scene, story — whatever you want." data-testid="wizard-bio" />
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 28, gap: 10 }}>
        <button className="btn btn-ghost" onClick={onBack}>← Back</button>
        <button className="btn btn-primary" onClick={onFinish} data-testid="wizard-finish">Finish &amp; Open Lacquer →</button>
      </div>
    </div>
  );
}
